// TODO: move the driver notebook here too

import { createErrorReportingAsyncThunk, withRetries } from "../../helpers";
import axios, { getData } from "../../../axios";
import * as tPromise from "io-ts-promise";
import { createSlice } from "@reduxjs/toolkit";
import {
  CargoTags,
  cargoTagsT,
  orgSettingsT,
  OrgSettings,
  NewShipmentDateOffset,
  calendarViewSettingsT,
} from "./types";
import { notifyL } from "../../notifications";
import { assign } from "lodash";
import {
  ArchiveRoutesUntilOption,
  CalendarViewSettings,
  DefaultDispatcherViewValue,
  InvoiceFolderOptions,
  invoiceFolderOptionsT,
} from "dora-contracts";

const prefix = "data/org-settings";

const initialState: OrgSettings = {
  cargoTags: [],
  newShipmentDate: "0",
  driverCanEditCargos: false,
  hideInvoiceRef: false,
  hideLoadmeters: false,
  clientRequiredOnCargos: false,
  hideCargoDescription: false,
  routeSearchInterval: "0",
  newSyncedCustomersVisibleToDriver: false,
  newSyncedProductsVisibleToDriver: false,
  showArrivedDepartureToDriver: false,
  showAddCubicMetersToDriver: false,
  showCargoTimeTrackingToDriver: false,
  hideCollectedDeliveredButtonsForDriver: false,
  driverCanAddExtraStopsDefault: false,
  driverCanPickFromUndispatchedDefault: false,
  hideCargoFromDriversDefault: false,
  defaultDispatcherView: "ROUTES_VIEW",
  automaticArchiveRoutesUntil: null,
  splitRouteCargoTeamId: null,
  splitCargosLabelId: null,
  fragmentCargosLabelId: null,
  calendarViewSettings: {
    firstRow: "POSTCODE_CITY",
    secondRow: "CARGO_DESCRIPTION",
    thirdRow: "CLIENT",
  },
  invoiceFolderOptions: { mode: "MANUAL" },
  equipmentEnabled: false,
  fractionsEnabled: false,
};

export const saveNewShipmentDate = createErrorReportingAsyncThunk(
  `${prefix}/saveNewShipmentDate`,
  async (shipmentDate: NewShipmentDateOffset, thunkAPI) => {
    return axios
      .put(`/api/org-settings/new-shipment-date`, { value: shipmentDate })
      .then(() => {
        thunkAPI.dispatch(
          notifyL({
            type: "success",
            namespace: "notifications",
            key: "shipmentDateSaved",
          })
        );
      });
  }
);

export const saveRouteSearchInterval = createErrorReportingAsyncThunk(
  `${prefix}/saveRouteSearchInterval`,
  async (routeSearchInterval: NewShipmentDateOffset, thunkAPI) => {
    return axios
      .put(`/api/org-settings/route-search-interval`, {
        value: routeSearchInterval,
      })
      .then(() => {
        thunkAPI.dispatch(
          notifyL({
            type: "success",
            namespace: "notifications",
            key: "routeSearchIntervalSaved",
          })
        );
      });
  }
);

export const saveAutomaticRouteArchivingInterval =
  createErrorReportingAsyncThunk(
    `${prefix}/saveAutomaticRouteArchivingInterval`,
    async (value: ArchiveRoutesUntilOption, thunkAPI) => {
      return axios
        .put(`/api/org-settings/archive-routes-until`, { value })
        .then(() => {
          thunkAPI.dispatch(
            notifyL({
              type: "success",
              namespace: "notifications",
              key: "settingsSaved",
            })
          );
        });
    }
  );

export const saveDispatcherDefaultViewValue = createErrorReportingAsyncThunk(
  `${prefix}/saveDispatcherDefaultView`,
  async (defaultDispatcherView: DefaultDispatcherViewValue, thunkAPI) => {
    return axios
      .put(`/api/org-settings/default-dispatcher-view`, {
        value: defaultDispatcherView,
      })
      .then(() => {
        thunkAPI.dispatch(
          notifyL({
            type: "success",
            namespace: "notifications",
            key: "settingsSaved",
          })
        );
      });
  }
);

export const loadCargoTags = createErrorReportingAsyncThunk(
  `${prefix}/loadCargoTags`,
  async () => {
    return axios
      .get(`/api/org-settings/cargo-tags`)
      .then(getData)
      .then(tPromise.decode(cargoTagsT));
  }
);

export const saveCargoTags = createErrorReportingAsyncThunk(
  `${prefix}/saveCargoTags`,
  async (cargoTags: CargoTags, thunkAPI) => {
    return axios.put(`/api/org-settings/cargo-tags`, cargoTags).then(() => {
      thunkAPI.dispatch(
        notifyL({
          type: "success",
          namespace: "notifications",
          key: "cargoTagsSaved",
        })
      );
    });
  }
);

export const saveHideLoadmetersValue = createErrorReportingAsyncThunk(
  `${prefix}/saveHideLoadmeters`,
  async (hideLoadmeters: boolean, thunkAPI) => {
    return axios
      .put(`/api/org-settings/hide-loadmeters`, { value: hideLoadmeters })
      .then(() => {
        thunkAPI.dispatch(
          notifyL({
            type: "success",
            namespace: "notifications",
            key: "hideLoadmetersSaved",
          })
        );
      });
  }
);

export const saveClientRequiredOnCargosValue = createErrorReportingAsyncThunk(
  `${prefix}/saveClientRequiredOnCargos`,
  async (clientRequiredOnCargos: boolean, thunkAPI) => {
    return axios
      .put(`/api/org-settings/client-required-on-cargos`, {
        value: clientRequiredOnCargos,
      })
      .then(() => {
        thunkAPI.dispatch(
          notifyL({
            type: "success",
            namespace: "notifications",
            key: "clientRequiredOnCargosSaved",
          })
        );
      });
  }
);

export const saveEquipmentEnabledValue = createErrorReportingAsyncThunk(
  `${prefix}/saveEquipmentEnabled`,
  async (value: boolean) => {
    await axios.put(`/api/org-settings/equipment-enabled`, { value });
  }
);

export const saveFractionsEnabledValue = createErrorReportingAsyncThunk(
  `${prefix}/saveFractionsEnabled`,
  async (value: boolean) => {
    await axios.put(`/api/org-settings/fractions-enabled`, { value });
  }
);

export const loadHideCargoDescriptionValue = createErrorReportingAsyncThunk(
  `${prefix}/loadHideCargoDescription`,
  async () => {
    return axios
      .get(`/api/org-settings/hide-cargo-description`)
      .then(getData)
      .then((x) => x.value);
  }
);

export const saveHideInvoiceRefValue = createErrorReportingAsyncThunk(
  `${prefix}/saveHideInvoiceRef`,
  async (hideInvoiceRef: boolean, thunkAPI) => {
    return axios
      .put(`/api/org-settings/hide-invoice-ref`, { value: hideInvoiceRef })
      .then(() => {
        thunkAPI.dispatch(
          notifyL({
            type: "success",
            namespace: "notifications",
            key: "hideInvoiceRefSaved",
          })
        );
      });
  }
);

export const saveHideCargoDescriptionValue = createErrorReportingAsyncThunk(
  `${prefix}/saveHideCargoDescription`,
  async (hideCargoDescription: boolean, thunkAPI) => {
    return axios
      .put(`/api/org-settings/hide-cargo-description`, {
        value: hideCargoDescription,
      })
      .then(() => {
        thunkAPI.dispatch(
          notifyL({
            type: "success",
            namespace: "notifications",
            key: "hideCargoDescriptionSaved",
          })
        );
      });
  }
);

export const loadOrgSettings = createErrorReportingAsyncThunk(
  `${prefix}/loadOrgSettings`,
  async () => {
    return withRetries(() =>
      axios
        .get(`/api/org-settings`)
        .then(getData)
        .then(tPromise.decode(orgSettingsT))
    );
  }
);

export const saveDriverCanEditCargosValue = createErrorReportingAsyncThunk(
  `${prefix}/saveDriverCanEditCargos`,
  async (driverCanEditCargos: boolean, thunkAPI) => {
    return axios
      .put(`/api/org-settings/driver-can-edit-cargos`, {
        value: driverCanEditCargos,
      })
      .then(() => {
        thunkAPI.dispatch(
          notifyL({
            type: "success",
            namespace: "notifications",
            key: "settingSaved",
          })
        );
      });
  }
);

export const saveToggleArrivedDepartedButtonsValue =
  createErrorReportingAsyncThunk(
    `${prefix}/saveToggleArrivedDepartedButtonsValue`,
    async (value: boolean) => {
      return axios
        .put(`/api/org-settings/toggle-arrived-departed-buttons`, { value })
        .then(() => value);
    }
  );

export const loadArrivedDepartedButtonsValue = createErrorReportingAsyncThunk(
  `${prefix}/loadArrivedDepartedButtonsValue`,
  async () => {
    return axios
      .get(`/api/org-settings/toggle-arrived-departed-buttons`)
      .then(getData)
      .then((x) => x.value);
  }
);

export const saveToggleAddCubicMetersButtonValue =
  createErrorReportingAsyncThunk(
    `${prefix}/saveToggleAddCubicMetersButtonValue`,
    async (value: boolean) => {
      return axios
        .put(`/api/org-settings/toggle-add-cubic-meters-button`, { value })
        .then(() => value);
    }
  );

export const saveToggleDriverCargoTimeTrackingValue =
  createErrorReportingAsyncThunk(
    `${prefix}/saveToggleDriverCargoTimeTrackingValue`,
    async (value: boolean) => {
      return axios
        .put(`/api/org-settings/toggle-driver-time-tracking`, { value })
        .then(() => value);
    }
  );

export const saveToggleHideCollectedDeliveredButtonsForDriverValue =
  createErrorReportingAsyncThunk(
    `${prefix}/saveToggleHideCollectedDeliveredButtonsForDriverValue`,
    async (value: boolean) => {
      return axios
        .put(`/api/org-settings/toggle-hide-collected-delivered-buttons`, {
          value,
        })
        .then(() => value);
    }
  );

export const saveDriverPickUndispatchedDefaultValue =
  createErrorReportingAsyncThunk(
    `${prefix}/saveDriverPickUndispatchedDefaultValue`,
    async (value: boolean) => {
      return axios
        .put(`/api/org-settings/driver-can-pick-from-undispatched-default`, {
          value,
        })
        .then(() => value);
    }
  );

export const saveDriverCanAddExtraStopsDefaultValue =
  createErrorReportingAsyncThunk(
    `${prefix}/saveDriverCanAddExtraStopsDefaultValue`,
    async (value: boolean) => {
      return axios
        .put(`/api/org-settings/driver-can-add-extra-stops-default`, {
          value,
        })
        .then(() => value);
    }
  );

export const saveHideCargoFromDriversDefaultValue =
  createErrorReportingAsyncThunk(
    `${prefix}/saveHideCargoFromDriversDefaultValue`,
    async (value: boolean) => {
      return axios
        .put(`/api/org-settings/hide-cargo-from-drivers-default`, { value })
        .then(() => value);
    }
  );

export const saveCalendarViewSettings = createErrorReportingAsyncThunk(
  `${prefix}/saveCalendarViewSettings`,
  async (value: CalendarViewSettings) => {
    return axios
      .put(
        `/api/org-settings/calendar-view-settings`,
        calendarViewSettingsT.encode(value)
      )
      .then(() => value);
  }
);

export const saveSplitRouteCargoTeamId = createErrorReportingAsyncThunk(
  `${prefix}/saveSplitRouteCargoTeamId`,
  async (value: string | null, thunkAPI) => {
    return axios
      .put(`/api/org-settings/split-route-cargo-team-id`, { value })
      .then(() => {
        thunkAPI.dispatch(
          notifyL({
            type: "success",
            namespace: "notifications",
            key: "settingsSaved",
          })
        );
        return value;
      });
  }
);

export const saveSplitCargosLabelId = createErrorReportingAsyncThunk(
  `${prefix}/saveSplitCargosLabelId`,
  async (value: string | null, thunkAPI) => {
    return axios
      .put(`/api/org-settings/split-cargos-label-id`, { value })
      .then(() => {
        thunkAPI.dispatch(
          notifyL({
            type: "success",
            namespace: "notifications",
            key: "settingsSaved",
          })
        );
        return value;
      });
  }
);

export const saveFragmentCargosLabelId = createErrorReportingAsyncThunk(
  `${prefix}/saveFragmentCargosLabelId`,
  async (value: string | null, thunkAPI) => {
    return axios
      .put(`/api/org-settings/fragment-cargos-label-id`, { value })
      .then(() => {
        thunkAPI.dispatch(
          notifyL({
            type: "success",
            namespace: "notifications",
            key: "settingsSaved",
          })
        );
        return value;
      });
  }
);

export const saveInvoiceFolderOptions = createErrorReportingAsyncThunk(
  `${prefix}/saveInvoiceFolderOptions`,
  async (value: InvoiceFolderOptions) => {
    return axios
      .put(
        `/api/org-settings/invoice-folder-options`,
        invoiceFolderOptionsT.encode(value)
      )
      .then(() => value);
  }
);

export const setNewSyncedClientsVisibleToDriver =
  createErrorReportingAsyncThunk(
    `${prefix}/setNewSyncedClientsVisibleToDriver`,
    async (value: boolean) => {
      return axios.put(
        `/api/org-settings/new-synced-customers-visible-to-driver`,
        {
          value,
        }
      );
    }
  );

export const setNewSyncedProductsVisibleToDriver =
  createErrorReportingAsyncThunk(
    `${prefix}/setNewSyncedProductsVisibleToDriver`,
    async (value: boolean) => {
      return axios.put(
        `/api/org-settings/new-synced-products-visible-to-driver`,
        {
          value,
        }
      );
    }
  );

export const loadAddCubicMetersButtonValue = createErrorReportingAsyncThunk(
  `${prefix}/loadAddCubicMetersButtonValue`,
  async () => {
    return axios
      .get(`/api/org-settings/toggle-add-cubic-meters-button`)
      .then(getData)
      .then((x) => x.value);
  }
);

const slice = createSlice({
  name: prefix,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loadOrgSettings.fulfilled, (state, action) => {
        assign(state, action.payload);
      })
      .addCase(loadCargoTags.fulfilled, (state, action) => {
        state.cargoTags = action.payload;
      })
      .addCase(loadHideCargoDescriptionValue.fulfilled, (state, action) => {
        state.hideCargoDescription = action.payload;
      })
      .addCase(setNewSyncedClientsVisibleToDriver.pending, (state, action) => {
        state.newSyncedCustomersVisibleToDriver = action.meta.arg;
      })
      .addCase(setNewSyncedClientsVisibleToDriver.rejected, (state, action) => {
        state.newSyncedCustomersVisibleToDriver = !action.meta.arg;
      })
      .addCase(setNewSyncedProductsVisibleToDriver.pending, (state, action) => {
        state.newSyncedProductsVisibleToDriver = action.meta.arg;
      })
      .addCase(setNewSyncedProductsVisibleToDriver.rejected, (state, action) => {
        state.newSyncedProductsVisibleToDriver = !action.meta.arg;
      })
      .addCase(saveSplitRouteCargoTeamId.fulfilled, (state, action) => {
        state.splitRouteCargoTeamId = action.payload;
      })
      .addCase(saveSplitCargosLabelId.fulfilled, (state, action) => {
        state.splitCargosLabelId = action.payload;
      })
      .addCase(saveFragmentCargosLabelId.fulfilled, (state, action) => {
        state.fragmentCargosLabelId = action.payload;
      });
  },
});

export default slice.reducer;
